import { i18n } from "@lingui/core";
import { plural, t } from "@lingui/core/macro";
// (dosipiuk): Disabled due to false-positives const exports using PascalCase
/* eslint-disable react-refresh/only-export-components */
import {
  EMPTY_STRING,
  type FrontendAllowedFileTypes,
  formatDateString,
  formatNumber,
  isValidEmail,
  isValidMultiEmail,
  isValidRestrictiveEmail,
  type ValuesUnion,
} from "@regrello/core-utils";
import { addMilliseconds, isAfter, isBefore, isEqual } from "date-fns";
import type { ValidationValueMessage } from "react-hook-form";

import { getFileExtension } from "../utils/documentUtil";

export const APP_VERSION = import.meta.env.VITE_APP_VERSION as string;

export const TASK_UUID_EXPECTED_FORMAT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export const DocumentsTableColumnName = {
  FILE_NAME: "fileName",
  ACTION: "action",
  WORKFLOW: "workflow",
  UPLOAD_DATE: "uploadDate",
  ADDED_BY: "addedBy",
  TAGS: "tags",
  DELETE: "delete",
} as const;

export type DocumentsTableColumnName = ValuesUnion<typeof DocumentsTableColumnName>;

export const CSS_FULL_WIDTH = "100%";

export const DEFAULT_DOCUMENTS_TABLE_COLUMN_WIDTHS_BY_ID: Record<DocumentsTableColumnName, number> = {
  fileName: 242,
  action: 222,
  workflow: 222,
  uploadDate: 166,
  addedBy: 180,
  // (clewis): Will be automeasured to fit all cell content. When the delete column is enabled, we
  // use 'flex: 1' instead of this width.
  tags: 100,
  delete: 50,
};

export const DEBOUNCE_TIMEOUT = 150;

// Useful for more expensive operations, such as LLM calls, that can wait longer
export const DEBOUNCE_TIMEOUT_LONG = 600;

export const MUTATION_TRACKING_TIMEOUT = 1000 * 30; // 30 seconds, same as our server request timeout

export const MESSAGE_HIGHLIGHT_AUTO_DISMISS_DURATION = 2000;

export const REGRELLO_PRIVACY_POLICY_URL = "https://www.regrello.com/privacy-policy";

export enum ResponseStatus {
  SUCCESS = 200,
  TOO_MANY_REQUESTS = 429,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
}

export const WORKFLOW_HEADER_HEIGHT_V2_CLASS = "sm:h-11";

export const SECONDS_IN_DAY = 60 * 60 * 24;
export const SECONDS_IN_HOUR = 60 * 60;
export const SECONDS_IN_MINUTE = 60;

export const MILLISECONDS_IN_MINUTE = 1000 * 60;
export const DAYS_IN_WEEK = 7;

export enum TimeUnit {
  DAYS = "Days",
  HOURS = "Hours",
  MINUTES = "Minutes",
}
export const TimeToCompleteUnits = [TimeUnit.DAYS, TimeUnit.HOURS, TimeUnit.MINUTES];

export const ValidationRules = {
  AFTER_DATE: (dateToCompare: Date) => ({
    validate: (value: Date | null) => {
      // (max): Null values are not validated; an empty value is neither before nor after.
      if (value == null || isEqual(value, dateToCompare) || isAfter(value, dateToCompare)) {
        return true;
      }
      const formattedDate = formatDateString(dateToCompare);
      return t`Must be after ${formattedDate}`;
    },
  }),
  BEFORE_DATE: (dateToCompare: Date) => ({
    validate: (value: Date | null) => {
      // (max): Null values are not validated; an empty value is neither before nor after.
      if (value == null || isEqual(value, dateToCompare) || isBefore(value, addMilliseconds(dateToCompare, 1))) {
        return true;
      }
      const formattedDate = formatDateString(dateToCompare);
      return t`Must be before ${formattedDate}`;
    },
  }),
  DOES_NOT_CONTAIN_SEMICOLON: {
    validate: (value: string) => (value.includes(";") ? t`Cannot contain ";" character` : true),
  },
  GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND: (lowerBound: number) => {
    const minValue = Math.max(lowerBound, Number.MIN_SAFE_INTEGER);
    const minValueFormatted = formatNumber(minValue, i18n.locale);
    return {
      min: {
        value: lowerBound,
        message: t`Must be at least ${minValueFormatted}`,
      },
    };
  },

  GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND_FN: (lowerBound: number) => {
    const rule = ValidationRules.GREATER_THAN_OR_EQUAL_TO_LOWER_BOUND(lowerBound);
    return {
      validate: (value: number) => {
        if (value < rule.min.value) {
          return rule.min.message;
        }
        return true;
      },
    };
  },

  INTEGER: {
    validate: {
      isInteger: (value: unknown) => {
        const minValue = Number.MIN_SAFE_INTEGER;
        const maxValue = Number.MAX_SAFE_INTEGER;

        const minValueFormatted = formatNumber(minValue, i18n.locale);
        const maxValueFormatted = formatNumber(maxValue, i18n.locale);

        // Handles various empty cases:
        // - Empty numeric inputs emit null to indicate an empty value.
        // - Empty legacy text inputs emit an empty string to indicate an empty value.
        // - useForm sets a field with an empty string when the corresponding default value is
        //   empty, this validation fails incorrectly.
        const isEmpty = value == null || value === EMPTY_STRING;

        return isEmpty
          ? true
          : Number.isInteger(Number(value))
            ? Number(value) > maxValue
              ? t`Must be less than or equal to ${maxValueFormatted}`
              : Number(value) < minValue
                ? t`Must be at least ${minValueFormatted}`
                : true
            : t`Must be a whole number`;
      },
    },
  },

  LESS_THAN_OR_EQUAL_TO_UPPER_BOUND: (upperBound: number) => {
    const maxValue = Math.min(upperBound, Number.MAX_VALUE);
    const maxValueFormatted = formatNumber(maxValue, i18n.locale);
    return {
      max: {
        value: upperBound,
        message: t`Must be less than or equal to ${maxValueFormatted}`,
      },
    };
  },

  LESS_THAN_OR_EQUAL_TO_UPPER_BOUND_FN: (upperBound: number) => {
    const rule = ValidationRules.LESS_THAN_OR_EQUAL_TO_UPPER_BOUND(upperBound);
    return {
      validate: (value: number) => {
        if (value > rule.max.value) {
          return rule.max.message;
        }
        return true;
      },
    };
  },

  /** Pass in `null` to turn off this validation rule. */
  MIN_LENGTH: (minLength: number | null) => {
    if (minLength == null) {
      return {
        minLength: undefined,
      };
    }
    // Extract to a variable to avoid having dynamic values in the translation string.
    const minLengthFormatted = formatNumber(minLength, i18n.locale);
    return {
      minLength: {
        value: minLength,
        message: t`Must be at least ${minLengthFormatted} characters`,
      },
    };
  },

  /** Pass in `null` to turn off this validation rule. */
  MIN_LENGTH_FN: (minLength: number | null) => {
    const rule = ValidationRules.MIN_LENGTH(minLength);
    return {
      validate: (value: string) => {
        if (rule.minLength == null) {
          return true;
        }
        if (value.length < rule.minLength.value) {
          return rule.minLength.message;
        }
        return true;
      },
    };
  },

  MAX_LENGTH: (maxLength: number) => {
    // Extract to a variable to avoid having dynamic values in the translation string.
    const formattedMaxLength = formatNumber(maxLength, i18n.locale);
    return {
      maxLength: {
        value: maxLength,
        message: t`Must be at most ${formattedMaxLength} characters`,
      },
    };
  },

  MAX_LENGTH_FN: (maxLength: number) => {
    const rule = ValidationRules.MAX_LENGTH(maxLength);
    return {
      validate: (value: string) => {
        if (rule.maxLength == null) {
          return true;
        }
        if (value.length > rule.maxLength.value) {
          return rule.maxLength.message;
        }
        return true;
      },
    };
  },

  /**
   * Fails open if the pattern is not valid
   */
  REGEX_FN: (pattern: string) => {
    try {
      const regex = new RegExp(pattern);

      return {
        validate: (value: string) => {
          return regex.test(value);
        },
        isPatternValid: () => true,
      };
    } catch {
      // Fail open: invalid regex means no validation
      return {
        validate: (_: string): true => true,
        isPatternValid: () => false,
      };
    }
  },

  NON_EMPTY_STRING_WHEN_TRIMMED: {
    validate: (value: string) => (value.trim().length > 0 ? true : t`Include a message`),
  },
  NOT_REQUIRED: {
    required: false,
    validate: () => true,
  },

  /**
   * Validates the provided number-typed value as being valid and within the range of safe
   * integers, or that the value is not defined.
   */
  NUMBER: {
    validate: {
      isNumber: (value: number | null | undefined) => {
        const minValue = Number.MIN_SAFE_INTEGER;
        const maxValue = Number.MAX_SAFE_INTEGER;

        const minValueFormatted = formatNumber(minValue, i18n.locale);
        const maxValueFormatted = formatNumber(maxValue, i18n.locale);

        if ((value as unknown) === EMPTY_STRING) {
          // (hchen): useForm may set a field with an empty string when the corresponding default
          // value is empty.
          throw new Error("isNumber: value is unexpectedly an empty string");
        }

        return value == null
          ? true
          : Number.isNaN(Number(value))
            ? t`Must be a number`
            : Number(value) > maxValue
              ? t`Must be less than or equal to ${maxValueFormatted}`
              : Number(value) < minValue
                ? t`Must be at least ${minValueFormatted}`
                : true;
      },
    },
  },

  /**
   * Validates the provided unknown-typed value as a valid number within the range of safe
   * integers, or that the value is not defined.
   */
  NUMBER_FROM_UNKNOWN: {
    validate: {
      isNumber: (value: unknown) => {
        const minValue = Number.MIN_SAFE_INTEGER;
        const maxValue = Number.MAX_SAFE_INTEGER;

        if (value == null || value === EMPTY_STRING) {
          return true;
        }

        if (Number.isNaN(Number(value))) {
          return t`Must be a number`;
        }

        if (Number(value) > maxValue) {
          // Extract to a variable to avoid having dynamic values in the translation string.
          const formattedMaxValue = formatNumber(maxValue, i18n.locale);
          return t`Must be less than or equal to ${formattedMaxValue}`;
        }

        if (Number(value) < minValue) {
          const formattedMinValue = formatNumber(minValue, i18n.locale);
          return t`Must be at least ${formattedMinValue}`;
        }

        return true;
      },
    },
  },
  POSITIVE_INTEGER_IN_RANGE: ({ min, max }: { min: number; max: number }) => {
    const minValue = Math.max(min, Number.MIN_SAFE_INTEGER);
    const maxValue = Math.min(max, Number.MAX_SAFE_INTEGER);

    const minValueFormatted = formatNumber(minValue, i18n.locale);
    const maxValueFormatted = formatNumber(maxValue, i18n.locale);

    return {
      min: {
        message: t`Must be a whole number between ${minValueFormatted} and ${maxValueFormatted}`,
        value: min,
      },
      max: {
        message: t`Must be a whole number between ${minValueFormatted} and ${maxValueFormatted}`,
        value: max,
      },
      pattern: {
        message: t`Must be a whole number between ${minValueFormatted} and ${maxValueFormatted}`,
        value: /^\d+$/, // Does not include negative numbers
      },
    };
  },
  REQUIRED: {
    validate: (value: unknown) => {
      if (typeof value !== "string") {
        return true;
      }
      return value.trim().length > 0 ? true : t`This field is required`;
    },
    // (swerner) This proxy is only required here as the `required` prop is created
    // at loadtime where the effective i18n.locale is not yet available.
    required: new Proxy<ValidationValueMessage<boolean>>(
      { value: true, message: "" },
      {
        get: (_obj, prop) => (prop === "message" ? t`This field is required` : prop === "value" ? true : undefined),
      },
    ) as ValidationValueMessage<boolean>,
  },
  REQUIRED_WITH_MESSAGE: (errorMessage: string) => {
    return {
      validate: (value: unknown) => {
        if (typeof value !== "string") {
          return true;
        }
        return value.trim().length > 0 ? true : errorMessage;
      },
      required: {
        value: true,
        message: errorMessage,
      },
    };
  },

  /**
   * Validate that the value does not exist in the provided string array. If `ignoreWhitespace` is
   * true, the values will be trimmed before comparing.
   */
  UNIQUE_IN: ({ ignoreWhitespace, otherValues }: { ignoreWhitespace?: boolean; otherValues: string[] }) => {
    return {
      validate: (value: string) => {
        return otherValues
          .map((otherValue) => (ignoreWhitespace ? otherValue.trim() : otherValue))
          .includes(ignoreWhitespace ? value.trim() : value)
          ? t`Must be a unique value`
          : true;
      },
    };
  },
  VALID_URL: {
    validate: (value: string) => {
      const browserUrlRegex = /^https?:\/\/[-\w@:%.+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-\w()@:%+,.~#?&/=]*$/;
      return browserUrlRegex.test(value) ? true : t`Must be a valid URL`;
    },
  },
  VALID_URL_WITH_TEMPLATE_VARS: {
    validate: (value: string) => {
      const browserUrlRegex = /^https?:\/\/[-\w@:%.+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-\w()@:%+,.~#?&/={}]*$/;
      return browserUrlRegex.test(value) ? true : t`Must be a valid URL`;
    },
  },
  VALID_URL_WITH_OPTIONAL_SCHEME: {
    validate: (value: string) => {
      let validateValue = value;
      // This validation should be kept in sync with utils/url.go
      const schemeRegex = /^[a-z]+:\/\//i;
      const browserUrlRegex = /^https?:\/\/[-\w@:%.+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-\w()@:%+,.~#?&/=]*$/;
      if (!schemeRegex.test(validateValue)) {
        validateValue = "https://" + validateValue;
      }
      return browserUrlRegex.test(validateValue) ? true : t`Must be a valid URL`;
    },
  },
  VALID_EMAIL: {
    validate: (value: string) => (isValidEmail(value) ? true : t`Email is invalid`),
  },

  /** Pass in `null` to turn off this validation rule. */
  VALID_BLUEPRINT_CONTACT_EMAIL_ALIAS: (emailDomain: string | null) => ({
    validate:
      emailDomain != null
        ? (value: string) =>
            isValidRestrictiveEmail(`${value}@${emailDomain}`)
              ? true
              : t`Email can only include letters (a-z), numbers, and periods.`
        : undefined,
  }),
  VALID_OR_EMPTY_EMAIL: {
    validate: (value: string) => (value.length === 0 ? true : isValidEmail(value) ? true : t`Email is invalid`),
  },
  VALID_MULTI_EMAIL: {
    validate: (value: string) =>
      value.length === 0 ? true : isValidMultiEmail(value) ? true : t`Email(s) are invalid`,
  },
  VALID_DOCUMENT_FORMAT: (acceptedFormats: FrontendAllowedFileTypes[]) => {
    return {
      validate: (values: Array<{ name: string; currentVersion: { externalLink?: string } }>) => {
        const acceptedFormatsStr = acceptedFormats.join(", ");
        return values.every((document) => {
          // (hchen): Don't validate links attached as documents.
          if (document.currentVersion.externalLink != null) {
            return true;
          }
          const documentType = getFileExtension(document.name);
          return documentType != null && acceptedFormats.includes(documentType as FrontendAllowedFileTypes);
        })
          ? true
          : plural(acceptedFormats.length, {
              one: `Only ${acceptedFormatsStr} is accepted.`,
              other: `Only ${acceptedFormatsStr} are accepted.`,
            });
      },
    };
  },

  /**
   * Validates that the value contains at least one letter.
   */
  HAS_ALPHA: {
    validate: (value: string) => (/[a-z]/i.test(value) ? true : t`Must contain at least one letter`),
  },

  // (hchen): Passing in undefined or null to `rule` doesn`t reset the rules automatically. We need
  // to either use this rule or unregister the field completely. However, unregistering will erase
  // the values too so it's not ideal.
  REMOVE_ALL_RULES: {
    required: false,
    min: Number.MIN_SAFE_INTEGER,
    max: Number.MAX_SAFE_INTEGER,
    minLength: -1,
    maxLength: Number.MAX_SAFE_INTEGER,
    pattern: /.*/,
    validate: () => true,
  },
};

export const WORKFLOW_TASK_LEAD_TIME_MIN_DAYS = 1;
export const WORKFLOW_TASK_LEAD_TIME_MAX_DAYS = 365;

/** How long to wait before polling for new data via a given GraphQL query. */
export enum DataRefreshPollInterval {
  DEFAULT = 10_000, // Realtime-ish.
  OCCASIONALLY = 30_000,
  OFTEN = 5_000,
  VERY_OFTEN = 1_000,
}

export enum RegrelloFieldControllerNames {
  REGRELLO_FIELD_CONTROLLER_ASSIGNEES = "RegrelloTaskAssignees",
  REGRELLO_FIELD_CONTROLLER_BACKUP_ASSIGNEES = "RegrelloTaskBackupAssignees",
  REGRELLO_FIELD_CONTROLLER_CC = "RegrelloTaskCc",
  REGRELLO_FIELD_CONTROLLER_DUE_ON = "RegrelloTaskDueOn",
}

// (zstanik): don't change these names unless the corresponding system fields are changed on the BE
// (which is highly unlikely since these fields already exist in a number of workspaces now). These
// are the exact names of the system fields as created upon running migrations in a new workspace.
// If these are changed, tests will fail too.
export const SUBMISSION_PDF_FIELD_NAME = "Submission PDF";
export const WORKFLOW_OWNER_FIELD_NAME = "Workflow owner";
export const WORKFLOW_REFERENCE_ID_FIELD_NAME = "Workflow Reference ID";
export const EMAIL_SYSTEM_FIELD_NAMES = [
  /* eslint-disable lingui/no-unlocalized-strings */
  "Requestor email",
  "Email attachments",
  "Email body",
  "Email body (PDF)",
  "Email subject",
  /* eslint-enable lingui/no-unlocalized-strings */
];

export const WORKFLOW_END_REASON_FIELD_NAME = "Workflow end reason";
/** Indicates that the column does not exist on the backend yet and needs to be created. */
export const UNDEFINED_COLUMN_ID = -1;

export const REGRELLO_SUPPORT_EMAIL = "support@regrello.com";

export const DISPLAY_COLUMN_LEFT = 0;
export const DISPLAY_COLUMN_RIGHT = 1;

export const WORKFLOW_END_REASON_MAXLENGTH = 20000;

/**
 * The maximum number of characters to render in the DOM for text field values. Values exceeding
 * this threshold are truncated for display, and a download button is shown instead. This is
 * critical for performance with huge text values.
 */
export const MAX_TEXT_FIELD_VALUE_CHARS = 10_000;
